import { minorUnitExponent } from '../../generated/billingCurrencyScales'
import type { Money } from './billingTypes'

/** DR's default pack price: exact minor units, active locale and an ISO suffix. */
export function formatPackPrice(money: Money, locale: string): string {
  const { amountMinor, currency } = money
  const fallback = `${amountMinor} ${currency}`
  if (!Number.isSafeInteger(amountMinor) || amountMinor < 0) return fallback
  try {
    // Intl chooses grouping, symbol placement and separators; the API owns the scale.
    const digits = minorUnitExponent(currency)
    const scale = 10n ** BigInt(digits)
    const minor = BigInt(amountMinor)
    const whole = minor / scale
    const fraction = minor % scale
    const fractionDigits = fraction === 0n ? 0 : digits
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })
    const parts = formatter.formatToParts(whole)
    let formatted: string
    if (fractionDigits === 0) {
      formatted = parts.map((part) => part.value).join('')
    } else {
      if (parts.filter((part) => part.type === 'fraction').length !== 1) return fallback
      const exact = fraction.toString().padStart(digits, '0')
      formatted = parts.map((part) => part.type === 'fraction' ? exact : part.value).join('')
    }
    return formatted.includes(currency) ? formatted : `${formatted} ${currency}`
  } catch {
    return fallback
  }
}
